import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';

export default function EmailDetailView() {
    const { category } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const [emails, setEmails] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const startDate = searchParams.get('start');
    const endDate = searchParams.get('end');

    useEffect(() => {
        const fetchDrilldownData = async () => {
            setLoading(true);
            setError('');

            try {
                const eventMap = {
                    delivered: 'delivered',
                    deliveries: 'delivered',
                    open: 'open',
                    opens: 'open',
                    opened: 'open',
                    click: 'click',
                    clicks: 'click',
                    clicked: 'click',
                    bounce: 'bounce',
                    bounces: 'bounce',
                    bounced: 'bounce',
                    dropped: 'dropped',
                    drops: 'dropped'
                };

                const eventType = eventMap[category] || category;

                const params = new URLSearchParams({
                    start_date: startDate,
                    end_date: endDate,
                    event: eventType,
                    page: page.toString()
                });


                const endpoint = process.env.REACT_APP_DRILLDOWN_ENDPOINT;
                const apiKey = process.env.REACT_APP_X_API_KEY;

                const headers = {};

                if (apiKey) {
                    headers['X-API-Key'] = apiKey;
                }

                const response = await fetch(
                    `${endpoint}?${params.toString()}`,
                    {
                        method: 'GET',
                        headers
                    }
                );

                if (!response.ok) {
                    throw new Error(`Request failed with status ${response.status}`);
                }

                const body = await response.json();
                const data = body.data;

                const events = (data?.events || []).map(event => ({
                    timestamp: event.timestamp,
                    recipient: event.policy_ref || event.quote_ref || '-',
                    subject: event.sg_template_name || event.scheme_name || 'Email Event',
                    status: event.event,
                    reason: event.details?.reason || null
                }));

                setEmails(events);
                setTotalPages(data?.total_pages || 1);
            } catch (error) {
                console.error('Failed to load drilldown data:', error);
                setEmails([]);
                setTotalPages(1);
                setError('Unable to load events. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        if (startDate && endDate && category) {
            fetchDrilldownData();
        }
    }, [category, startDate, endDate, page]);

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <button 
                    onClick={() => navigate(-1)}
                    className="p-2 hover:bg-gray-200 rounded-md transition-colors text-gray-600"
                >
                    <ArrowLeft className="h-5 w-5" />
                </button>

                <div>
                    <h1 className="text-2xl font-bold text-gray-900 capitalize">
                        {category.replace('_', ' ')} Events
                    </h1>

                    <p className="text-sm text-gray-500">
                        {startDate} to {endDate}
                    </p>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Timestamp
                                </th>

                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Recipient
                                </th>

                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Subject
                                </th>

                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status / Reason
                                </th>
                            </tr>
                        </thead>

                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td
                                        colSpan="4"
                                        className="px-6 py-4 text-center text-sm text-gray-500"
                                    >
                                        Loading events...
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td
                                        colSpan="4"
                                        className="px-6 py-4 text-center text-sm text-red-600"
                                    >
                                        {error}
                                    </td>
                                </tr>
                            ) : emails.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan="4"
                                        className="px-6 py-4 text-center text-sm text-gray-500"
                                    >
                                        No events found for this criteria.
                                    </td>
                                </tr>
                            ) : (
                                emails.map((email, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(email.timestamp).toLocaleString('en-GB')}
                                        </td>

                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {email.recipient}
                                        </td>

                                        <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">
                                            {email.subject}
                                        </td>

                                        <td className="px-6 py-4 text-sm text-gray-500">
                                            {email.reason || email.status || '-'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                        Page {page} of {totalPages}
                    </span>

                    <div className="flex gap-2">
                        <button 
                            disabled={page <= 1 || loading}
                            onClick={() => setPage(p => p - 1)}
                            className="p-1 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-200"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>

                        <button 
                            disabled={page >= totalPages || loading}
                            onClick={() => setPage(p => p + 1)}
                            className="p-1 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-200"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}